import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'
import FieldHelp from '../../components/FieldHelp'

interface RecurringJournalEntry {
  id: string
  name: string
  description?: string
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY'
  startDate: string
  endDate?: string
  nextExecutionDate: string
  isActive: boolean
  reference?: string
  executionCount: number
  lastExecutedAt?: string
  lines: RecurringJournalEntryLine[]
  creator: {
    firstName: string
    lastName: string
  }
  generatedEntries?: Array<{
    id: string
    entryNumber: string
    entryDate: string
    status: string
  }>
}

interface RecurringJournalEntryLine {
  id: string
  accountId: string
  debit: string
  credit: string
  description?: string
  account: {
    code: string
    name: string
  }
}

const RecurringJournalEntries: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission } = usePermissions(user)
  const [entries, setEntries] = useState<RecurringJournalEntry[]>([])
  const [accounts, setAccounts] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedEntry, setSelectedEntry] = useState<RecurringJournalEntry | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    frequency: 'MONTHLY' as 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    reference: '',
    lines: [
      { accountId: '', debit: '', credit: '', description: '' },
      { accountId: '', debit: '', credit: '', description: '' },
    ] as Array<{ accountId: string; debit: string; credit: string; description: string }>,
  })

  useEffect(() => {
    fetchEntries()
    fetchAccounts()
  }, [])

  const fetchEntries = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/recurring-journal-entries', {
        params: { includeInactive: 'true' },
      })
      setEntries(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching recurring journal entries:', error)
      toast.error('Failed to load recurring journal entries')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/erp/accounts', { params: { isActive: 'true' } })
      setAccounts(response.data.data || [])
    } catch (error) {
      console.error('Error fetching accounts:', error)
    }
  }

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { accountId: '', debit: '', credit: '', description: '' }],
    })
  }

  const removeLine = (index: number) => {
    if (formData.lines.length > 2) {
      setFormData({
        ...formData,
        lines: formData.lines.filter((_, i) => i !== index),
      })
    }
  }

  const updateLine = (index: number, field: string, value: string) => {
    const newLines = [...formData.lines]
    if (field === 'debit' || field === 'credit') {
      // Ensure only one of debit/credit has a value
      if (field === 'debit') {
        newLines[index] = { ...newLines[index], debit: value, credit: '' }
      } else {
        newLines[index] = { ...newLines[index], credit: value, debit: '' }
      }
    } else {
      newLines[index] = { ...newLines[index], [field]: value }
    }
    setFormData({ ...formData, lines: newLines })
  }

  const calculateBalance = () => {
    const totalDebit = formData.lines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0)
    const totalCredit = formData.lines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0)
    return { totalDebit, totalCredit, difference: totalDebit - totalCredit }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const balance = calculateBalance()
    if (Math.abs(balance.difference) > 0.01) {
      toast.error(`Transaction is not balanced. Difference: ${balance.difference.toFixed(2)}`)
      return
    }

    // Validate all lines have accounts
    for (let i = 0; i < formData.lines.length; i++) {
      if (!formData.lines[i].accountId) {
        toast.error(`Line ${i + 1}: Account is required`)
        return
      }
      if (!formData.lines[i].debit && !formData.lines[i].credit) {
        toast.error(`Line ${i + 1}: Either debit or credit must be entered`)
        return
      }
    }

    try {
      const payload = {
        name: formData.name,
        description: formData.description || undefined,
        frequency: formData.frequency,
        startDate: formData.startDate,
        endDate: formData.endDate || null,
        reference: formData.reference || undefined,
        lines: formData.lines.map((line) => ({
          accountId: line.accountId,
          debit: parseFloat(line.debit) || 0,
          credit: parseFloat(line.credit) || 0,
          description: line.description || undefined,
        })),
      }

      await axios.post('/api/erp/recurring-journal-entries', payload)
      toast.success('Recurring journal entry created successfully')
      setShowCreateModal(false)
      resetForm()
      fetchEntries()
    } catch (error: any) {
      console.error('Error creating recurring journal entry:', error)
      toast.error(error.response?.data?.error || 'Failed to create recurring journal entry')
    }
  }

  const handlePause = async (id: string) => {
    try {
      await axios.post(`/api/erp/recurring-journal-entries/${id}/pause`)
      toast.success('Recurring journal entry paused')
      fetchEntries()
    } catch (error: any) {
      console.error('Error pausing recurring journal entry:', error)
      toast.error(error.response?.data?.error || 'Failed to pause recurring journal entry')
    }
  }

  const handleResume = async (id: string) => {
    try {
      await axios.post(`/api/erp/recurring-journal-entries/${id}/resume`)
      toast.success('Recurring journal entry resumed')
      fetchEntries()
    } catch (error: any) {
      console.error('Error resuming recurring journal entry:', error)
      toast.error(error.response?.data?.error || 'Failed to resume recurring journal entry')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this recurring journal entry? This can only be done if no journal entries have been generated yet.')) {
      return
    }

    try {
      await axios.delete(`/api/erp/recurring-journal-entries/${id}`)
      toast.success('Recurring journal entry deleted')
      fetchEntries()
    } catch (error: any) {
      console.error('Error deleting recurring journal entry:', error)
      toast.error(error.response?.data?.error || 'Failed to delete recurring journal entry')
    }
  }

  const handleView = async (id: string) => {
    try {
      setIsLoading(true)
      const response = await axios.get(`/api/erp/recurring-journal-entries/${id}`)
      setSelectedEntry(response.data.data)
      setShowViewModal(true)
    } catch (error: any) {
      console.error('Error fetching recurring journal entry:', error)
      toast.error('Failed to load recurring journal entry details')
    } finally {
      setIsLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      frequency: 'MONTHLY',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      reference: '',
      lines: [
        { accountId: '', debit: '', credit: '', description: '' },
        { accountId: '', debit: '', credit: '', description: '' },
      ],
    })
  }

  const getFrequencyLabel = (freq: string) => {
    const labels: Record<string, string> = {
      DAILY: 'Daily',
      WEEKLY: 'Weekly',
      MONTHLY: 'Monthly',
      QUARTERLY: 'Quarterly',
      YEARLY: 'Yearly',
    }
    return labels[freq] || freq
  }

  const balance = calculateBalance()

  if (!hasPermission('erp_journal_entry', 'read')) {
    return (
      <div className="p-6">
        <p className="text-red-800">You don't have permission to view recurring journal entries.</p>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Recurring Journal Entries</h1>
          <PageHelp pageIdentifier="recurring-journal-entries" title="Recurring Journal Entries" />
        </div>
        {hasPermission('erp_journal_entry', 'write') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Create Recurring Entry
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="mt-2 text-gray-600">Loading recurring journal entries...</p>
      ) : entries.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-600">No recurring journal entries found. Create your first recurring entry to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Execution</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Executions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleView(entry.id)}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{entry.name}</div>
                    {entry.description && <div className="text-sm text-gray-500">{entry.description}</div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{getFrequencyLabel(entry.frequency)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(entry.startDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(entry.nextExecutionDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.executionCount}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        entry.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {entry.isActive ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2">
                      {entry.isActive ? (
                        hasPermission('erp_journal_entry', 'write') && (
                          <button
                            onClick={() => handlePause(entry.id)}
                            className="text-yellow-600 hover:text-yellow-900"
                          >
                            Pause
                          </button>
                        )
                      ) : (
                        hasPermission('erp_journal_entry', 'write') && (
                          <button
                            onClick={() => handleResume(entry.id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Resume
                          </button>
                        )
                      )}
                      {hasPermission('erp_journal_entry', 'delete') && (
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create Recurring Journal Entry</h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Frequency *</label>
                    <select
                      value={formData.frequency}
                      onChange={(e) =>
                        setFormData({ ...formData, frequency: e.target.value as any })
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="DAILY">Daily</option>
                      <option value="WEEKLY">Weekly</option>
                      <option value="MONTHLY">Monthly</option>
                      <option value="QUARTERLY">Quarterly</option>
                      <option value="YEARLY">Yearly</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date (Optional)</label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                    <input
                      type="text"
                      value={formData.reference}
                      onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">Entry Lines *</label>
                    <button
                      type="button"
                      onClick={addLine}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      + Add Line
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.lines.map((line, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-start">
                        <div className="col-span-4">
                          <select
                            value={line.accountId}
                            onChange={(e) => updateLine(index, 'accountId', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                            required
                          >
                            <option value="">Select Account</option>
                            {accounts
                              .filter((acc) => !acc.isGroup)
                              .map((acc) => (
                                <option key={acc.id} value={acc.id}>
                                  {acc.code} - {acc.name}
                                </option>
                              ))}
                          </select>
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={line.debit}
                            onChange={(e) => updateLine(index, 'debit', e.target.value)}
                            placeholder="Debit"
                            className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                            disabled={!!line.credit}
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={line.credit}
                            onChange={(e) => updateLine(index, 'credit', e.target.value)}
                            placeholder="Credit"
                            className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                            disabled={!!line.debit}
                          />
                        </div>
                        <div className="col-span-3">
                          <input
                            type="text"
                            value={line.description}
                            onChange={(e) => updateLine(index, 'description', e.target.value)}
                            placeholder="Description"
                            className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                          />
                        </div>
                        <div className="col-span-1">
                          {formData.lines.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeLine(index)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-sm text-gray-600">
                    Total Debit: {balance.totalDebit.toFixed(2)} | Total Credit: {balance.totalCredit.toFixed(2)} |{' '}
                    <span className={balance.difference === 0 ? 'text-green-600' : 'text-red-600'}>
                      Difference: {balance.difference.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    resetForm()
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{selectedEntry.name}</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Frequency</label>
                  <p className="text-gray-900">{getFrequencyLabel(selectedEntry.frequency)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      selectedEntry.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {selectedEntry.isActive ? 'Active' : 'Paused'}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <p className="text-gray-900">{new Date(selectedEntry.startDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <p className="text-gray-900">
                    {selectedEntry.endDate ? new Date(selectedEntry.endDate).toLocaleDateString() : 'No end date'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Next Execution</label>
                  <p className="text-gray-900">{new Date(selectedEntry.nextExecutionDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Executions</label>
                  <p className="text-gray-900">{selectedEntry.executionCount}</p>
                </div>
              </div>

              {selectedEntry.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <p className="text-gray-900">{selectedEntry.description}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Entry Lines</label>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedEntry.lines.map((line, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm">
                          {line.account.code} - {line.account.name}
                        </td>
                        <td className="px-4 py-2 text-sm text-right">
                          {parseFloat(line.debit) > 0 ? parseFloat(line.debit).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' }) : '-'}
                        </td>
                        <td className="px-4 py-2 text-sm text-right">
                          {parseFloat(line.credit) > 0 ? parseFloat(line.credit).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' }) : '-'}
                        </td>
                        <td className="px-4 py-2 text-sm">{line.description || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selectedEntry.generatedEntries && selectedEntry.generatedEntries.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Generated Journal Entries (Last 10)</label>
                  <div className="space-y-1">
                    {selectedEntry.generatedEntries.map((entry) => (
                      <div key={entry.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <span className="text-sm">
                          {entry.entryNumber} - {new Date(entry.entryDate).toLocaleDateString()}
                        </span>
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            entry.status === 'POSTED' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {entry.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedEntry(null)
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RecurringJournalEntries
